import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";

/**
 * React Router applies a route swap inside `React.startTransition`, so `navigate()`
 * returning does not mean the new screen is in the DOM yet. A view transition wrapped
 * around `navigate()` therefore captures the *old* screen as both its "old" and "new"
 * state, animates nothing, and is torn down on the next frame when React's commit
 * finally lands — which is how the slide animations went missing. The 100ms sleep the
 * transitions used to carry inside their update callback was covering this gap; this
 * waits for the commit itself instead of guessing at its duration.
 */
const COMMIT_TIMEOUT_MS = 300;

let waiting: Array<() => void> = [];

/** Resolves once React has committed a render at a new location, or gives up on timeout. */
export function awaitNavigationCommit(): Promise<void> {
  // Under native navigation a push builds a new native view rather than re-rendering this
  // one: the view's Router location is fixed for its lifetime, so there is no commit
  // coming and the probe below never fires. Waiting would just stall every push until the
  // timeout. UIKit animates the push there; the CSS slides are web and Android only.
  if (isIosNativeNavigation()) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      waiting = waiting.filter((w) => w !== settle);
      resolve();
    };

    waiting.push(settle);
    setTimeout(settle, COMMIT_TIMEOUT_MS);
  });
}

function notifyNavigationCommit() {
  const settling = waiting;
  waiting = [];
  settling.forEach((settle) => settle());
}

/**
 * Mounted inside the router by `GlobalRouter`. A layout effect fires once React has put
 * the new location on screen, which is the moment the browser may snapshot it.
 */
export const NavigationCommitProbe = () => {
  const location = useLocation();
  const committedKey = useRef(location.key);

  useLayoutEffect(() => {
    if (committedKey.current === location.key) return;
    committedKey.current = location.key;
    notifyNavigationCommit();
  }, [location.key]);

  return null;
};
